/**
 * Main's inbox, from the port's threads (MX-08; P-FE hand-off (b)).
 *
 * Main's message widgets (FloatingMessageWidget, MobileMessageWidget) post
 * `Messages.FetchAllMessagesByIDS` and get back keyon's FLAT rows: one row per
 * message per recipient, every row the signed-in person sent or received, with
 * `pd` (the row id), `sender_id` / `recipient_id` and their names, `message`,
 * `subject`, `msgStatus` ('NEW' while unread), the group fields
 * (`is_group_message`, `group_id`, `group_participants`), the patient focus,
 * and `msgTimestamp` / `msgDate`. The widgets build the conversations from
 * those rows in the browser (groupMessagesByThread): a direct conversation is
 * the pair plus the patient focus, a group one its `group_id`; unread counts
 * are the 'NEW' rows; a group's member names come from the rows naming them.
 *
 * The port keeps threads on the server (`GET /message-threads`, each with its
 * participants and patient focus) and their messages under them, stored once
 * with a receipt per recipient. Handing the widgets thread rows left the inbox
 * empty. This rebuilds keyon's rows from them, so the widgets' own threading
 * lands each message back in the conversation the server keeps it in:
 *
 *  - `pd` is the message id, which MarkMessagesAsRead sends back; a group
 *    message the viewer sent is one row per other member, as keyon stored it
 *    (the later rows' `pd` carry the member too, so the widgets' de-duplication
 *    by `pd` keeps them, and their own group de-duplication shows the message
 *    once). That is what names every member who never wrote.
 *  - `group_id` is the thread's id, which SendGroupMessage posts a reply to.
 *    A direct reply goes back through SendInternalMessages with the other
 *    person and the focus, and the server appends it to the same thread.
 *  - `msgStatus` is the viewer's own receipt: 'NEW' unread, 'read' otherwise
 *    (their own messages have none).
 *  - `msgTimestamp` is `sent_at` (ISO 8601 with its offset) and `msgDate` its
 *    day where the viewer is (bridgeMap withMessageTimes), which
 *    utils/messageTimestamp.js renders as "Today · 9:41 AM".
 *
 * A thread's messages are read only when the thread changed since the last
 * poll (its last message time, message count or the viewer's unread count),
 * so the widgets' 30-second poll costs the one thread-list request in the
 * steady state, not one per conversation. The copy lives in this page's
 * memory only, for the signed-in person: another viewer drops it, a
 * conversation no longer listed is dropped, and signing out leaves the page.
 */
import api from './client';

/** The thread list the widgets poll; the API's largest page. */
const THREAD_PAGE = { sort: '-last_message_at', per_page: 100, include: 'participants,patient_focus' };

/** A thread's messages, newest first, a page at a time (the widgets order them themselves). */
const MESSAGE_PAGE = { sort: '-sent_at', per_page: 100 };

/** How many pages of one thread are read (10 000 messages); beyond it the oldest are left out. */
const MAX_MESSAGE_PAGES = 100;

/** Threads read at once when several changed together. */
const CONCURRENCY = 4;

const cache = { viewer: null, threads: new Map() };

function str(value) {
  return value === undefined || value === null ? '' : String(value);
}

/** Drop every remembered conversation (sign-out, tests). */
export function forgetInbox() {
  cache.viewer = null;
  cache.threads = new Map();
}

/** The thread's people who are users, in the order the API lists them. */
function membersOf(thread) {
  return (Array.isArray(thread.participants) ? thread.participants : [])
    .filter((p) => p && p.user_id !== null && p.user_id !== undefined)
    .map((p) => ({
      id: str(p.user_id),
      name: str(p.user && p.user.name).trim(),
      active: !p.left_at,
    }));
}

function focusName(thread) {
  const focus = thread.patient_focus;
  if (!focus) return '';
  return `${str(focus.first_name)} ${str(focus.last_name)}`.trim();
}

/**
 * keyon's rows for one thread's messages, as the signed-in person
 * (`viewerId`) received or sent them. Pure.
 *
 * @param {object} thread a MessageThreadResource with `participants` (and `patient_focus`)
 * @param {object[]} messages its MessageResources, each already carrying main's
 *   `thread_attachments` and `msgTimestamp` / `msgDate` (the bridge decorates them)
 * @param {string|number} viewerId the signed-in person's user id
 */
export function inboxRowsForThread(thread, messages, viewerId) {
  const viewer = str(viewerId);
  const members = membersOf(thread);
  const nameOf = (userId) => (members.find((m) => m.id === str(userId)) || {}).name || '';
  const isGroup = thread.kind === 'group';
  const groupParticipants = members.filter((m) => m.active || m.id === viewer).map((m) => m.id);
  const others = members.filter((m) => m.id !== viewer);
  const focusId = thread.patient_focus_id === null || thread.patient_focus_id === undefined
    ? null
    : thread.patient_focus_id;
  const focus = focusName(thread);

  const rows = [];

  (messages || []).forEach((message) => {
    const sender = str(message.sender_id);
    const base = {
      ...message,
      pd: message.id,
      thread_id: thread.id,
      subject: str(thread.subject),
      message: str(message.body),
      msgStatus: message.is_read === false ? 'NEW' : 'read',
      sender_id: sender,
      sender_name: str(message.sender && message.sender.name).trim() || nameOf(sender),
      patient_focus_id: message.patient_focus_id ?? focusId,
      patient_focus_name: focus,
      is_group_message: isGroup ? 1 : 0,
      group_id: isGroup ? thread.id : null,
      group_participants: isGroup ? groupParticipants.join(',') : '',
    };

    if (sender !== viewer) {
      rows.push({ ...base, recipient_id: viewer, recipient_name: nameOf(viewer) });
      return;
    }

    if (!isGroup) {
      const other = others[0] || { id: '', name: '' };
      rows.push({ ...base, recipient_id: other.id, recipient_name: other.name });
      return;
    }

    // keyon stored a group message once per recipient.
    const recipients = others.filter((m) => m.active);
    (recipients.length ? recipients : [{ id: '', name: '' }]).forEach((recipient, index) => {
      rows.push({
        ...base,
        pd: index === 0 ? message.id : `${message.id}:${recipient.id}`,
        recipient_id: recipient.id,
        recipient_name: recipient.name,
      });
    });
  });

  return rows;
}

/** What changes when a thread does: a new message, or the viewer reading one. */
function stampOf(thread) {
  return [thread.last_message_at, thread.message_count, thread.unread_count].map(str).join('|');
}

async function messagesOf(thread) {
  const all = [];

  for (let page = 1; page <= MAX_MESSAGE_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result = await api.list(`/message-threads/${thread.id}/messages`, { ...MESSAGE_PAGE, page });
    all.push(...(result.items || []));

    const meta = result.meta || {};
    if (!meta.last_page || Number(meta.current_page || page) >= Number(meta.last_page)) break;
  }

  return all;
}

/** Run `work` over `items`, `limit` at a time, keeping their order. */
async function eachLimited(items, limit, work) {
  const results = new Array(items.length);
  let next = 0;

  async function lane() {
    while (next < items.length) {
      const index = next;
      next += 1;
      // eslint-disable-next-line no-await-in-loop
      results[index] = await work(items[index]);
    }
  }

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, lane));
  return results;
}

/**
 * keyon's FetchAllMessagesByIDS rows for the signed-in person.
 *
 * @param {object} options
 * @param {string|number} options.viewerId the signed-in person's user id
 * @param {(message: object) => object} [options.decorate] main's per-message
 *   fields the bridge adds (attachments, times)
 */
export async function fetchInboxRows({ viewerId, decorate = (m) => m } = {}) {
  const viewer = str(viewerId);
  if (!viewer) return [];

  if (cache.viewer !== viewer) {
    forgetInbox();
    cache.viewer = viewer;
  }

  const page = await api.list('/message-threads', THREAD_PAGE);
  const threads = page.items || [];

  const perThread = await eachLimited(threads, CONCURRENCY, async (thread) => {
    const stamp = stampOf(thread);
    const known = cache.threads.get(thread.id);

    const messages = known && known.stamp === stamp
      ? known.messages
      : (await messagesOf(thread)).map(decorate);

    // Read again only when this viewer is still the one signed in.
    if (cache.viewer === viewer) cache.threads.set(thread.id, { stamp, messages });

    return inboxRowsForThread(thread, messages, viewer);
  });

  // A conversation the viewer no longer sees is not remembered either.
  const listed = new Set(threads.map((thread) => thread.id));
  Array.from(cache.threads.keys()).forEach((key) => {
    if (!listed.has(key)) cache.threads.delete(key);
  });

  return perThread.flat();
}
